/**
 * **What one screen already read, handed to the next one instead of read again** — R5 steps 5
 * and 6.
 *
 * Gate Detail issues `GET /gates/{gateId}` and `GET /gates/{gateId}/members` at mount; the Access
 * page needs both answers and used to ask for them again, as did People one screen further on.
 * The Access page in turn prefetches Activity's first page and the שעון שבת config while the
 * manager is reading it, so neither screen opens on a spinner. This module is the seam all four
 * of those handoffs pass through.
 *
 * **Module state, not a route param, on `pendingToast.ts`'s own argument.** The route's shape is
 * `flows.md`'s map and a roster is not a location — `/gates/{id}/people` means the same roster
 * whether it was reached from the Access page or from a deep link.
 *
 * Five rules, each of which something depends on:
 *
 * 1. **Gate-scoped, and a mismatch answers nothing.** A deposit for one gate is never handed to a
 *    screen showing another, which is `takePendingGateToast`'s rule and the reason both are keyed
 *    the same way. A deposit for a *different* gate replaces the entry outright: there is exactly
 *    one navigation in flight.
 * 2. **Per-slot, and taking one leaves the others.** The Access page takes `gate` and `roster` and
 *    must not thereby destroy the Activity page it has itself just deposited. A whole-entry
 *    `take` was the first shape and is the one that cannot express this.
 * 3. **Read-once.** A `take` clears the slot it returns, so a screen re-entered later — a back
 *    tap, a second visit — issues its own read rather than rendering an answer from a previous
 *    visit. **The handoff bridges one navigation already in flight; it is not a cache and must not
 *    become one.** `device-cache/` is where anything that outlives a navigation belongs, under an
 *    allow-list this module deliberately does not have.
 * 4. **A miss is not an error.** Every consumer falls back to its own read, so a deep link
 *    straight into the Access page, People, Activity or שעון שבת behaves exactly as it did before
 *    this existed — and so does a manager who taps through faster than the deposit arrives.
 * 5. **Not persisted.** A module-level variable rather than `sessionStorage`: a `GateDetail`, a
 *    `Roster` and an activity page each carry more than the snapshot allow-list permits at rest (a
 *    manager's `location.lat`/`lng`, a roster's phone numbers, an audit trail), and nothing here is
 *    worth restoring anyone to across a reload.
 */
import type { GateId } from '@selogate/core'
import type { ActivityRow, GateDetail, Roster, ShabbatConfig } from './api'

/** What the Activity screen's own page walk answers with — deposited whole, verdict included. */
export type ActivityPageHandoff =
  | { readonly kind: 'page'; readonly rows: readonly ActivityRow[]; readonly nextCursor?: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'failed' }

/**
 * The שעון שבת prefetch, as the panel's own load treats it: a config, or the refusal that came
 * back. A *failed* prefetch is deliberately **not** deposited — see `AccessScreen`'s prefetch —
 * so this carries only answers the screen can act on without a second round trip.
 */
export type ShabbatConfigHandoff =
  { readonly kind: 'config'; readonly config: ShabbatConfig } | { readonly kind: 'gone' }

export interface GateHandoff {
  readonly gate?: GateDetail
  readonly roster?: Roster
  readonly activity?: ActivityPageHandoff
  readonly shabbat?: ShabbatConfigHandoff
}

let entry: { readonly gateId: GateId; readonly value: GateHandoff } | undefined

/**
 * Deposit what this screen holds for the one it is navigating to.
 *
 * Merges into an existing deposit for the **same** gate rather than replacing it, so reads that
 * resolve independently and in either order — the gate read and the roster read, the two Access
 * page prefetches — can each deposit as they arrive without the later one erasing the earlier.
 */
export function putGateHandoff(gateId: GateId, patch: GateHandoff): void {
  entry =
    entry !== undefined && entry.gateId === gateId
      ? { gateId, value: { ...entry.value, ...patch } }
      : { gateId, value: patch }
}

/**
 * Consume one slot of the deposit for this gate. Returns `undefined` on a gate mismatch, on an
 * empty slot, and on every later call for the same slot.
 */
export function takeGateHandoff<K extends keyof GateHandoff>(
  gateId: GateId,
  slot: K,
): GateHandoff[K] | undefined {
  if (entry === undefined || entry.gateId !== gateId) return undefined
  const held = entry.value[slot]
  if (held === undefined) return undefined
  const { [slot]: _taken, ...rest } = entry.value
  entry = { gateId, value: rest }
  return held
}

/** Test seam only — the module state is otherwise unreachable between cases. */
export function clearGateHandoff(): void {
  entry = undefined
}
